/**
 * PollenDotsInvestors — dashboard tab for the Investor Brief page.
 * Curate PDFs, edit the fixed cover note, copy the open share link, and
 * mint per-investor tracked links with view stats.
 */
import React, { useEffect, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Copy, ExternalLink, Upload, Trash2, ArrowUp, ArrowDown,
  Link2, Eye, Landmark, Check, Pencil, Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import { SHARE_DOMAIN as SHARE_ORIGIN } from '@/lib/shareDomain';

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error('Could not copy — long-press the link to copy manually');
  }
};

export default function PollenDotsInvestors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headline, setHeadline] = useState('');
  const [coverNote, setCoverNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newInvestor, setNewInvestor] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [editingFile, setEditingFile] = useState(null); // {id, title}
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/pollen/investor/overview`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setHeadline(d.page?.headline || '');
        setCoverNote(d.page?.cover_note || '');
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      const res = await fetch(`${API}/pollen/investor/page`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ headline, cover_note: coverNote }),
      });
      if (res.ok) toast.success('Cover note saved');
      else toast.error('Could not save cover note');
    } finally { setSavingNote(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/pollen/investor/files/upload`, {
        method: 'POST', credentials: 'include', headers: authHeaders(), body: fd,
      });
      if (res.ok) { toast.success('PDF added to investor page'); await load(); }
      else toast.error((await res.json()).detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const renameFile = async () => {
    if (!editingFile?.title?.trim()) return;
    const res = await fetch(`${API}/pollen/investor/files/${editingFile.id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ title: editingFile.title.trim() }),
    });
    if (res.ok) { toast.success('Renamed'); setEditingFile(null); await load(); }
  };

  const moveFile = async (idx, dir) => {
    const files = [...(data?.files || [])];
    const j = idx + dir;
    if (j < 0 || j >= files.length) return;
    [files[idx], files[j]] = [files[j], files[idx]];
    await Promise.all(files.map((f, i) => fetch(`${API}/pollen/investor/files/${f.id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ sort_order: i }),
    })));
    await load();
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Remove "${f.title}" from the investor page?`)) return;
    const res = await fetch(`${API}/pollen/investor/files/${f.id}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) { toast.success('Removed'); await load(); }
  };

  const createLink = async () => {
    if (!newInvestor.trim()) { toast.error('Enter the investor name'); return; }
    setCreatingLink(true);
    try {
      const res = await fetch(`${API}/pollen/investor/links`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ investor_name: newInvestor.trim() }),
      });
      if (res.ok) {
        const link = await res.json();
        setNewInvestor('');
        await copyText(shareNoteFor(link.investor_name, trackedShareUrl(link.slug)),
                        `Share note for ${link.investor_name}`);
        await load();
      }
    } finally { setCreatingLink(false); }
  };

  const revokeLink = async (l) => {
    if (!window.confirm(`Revoke the link for ${l.investor_name}? They will no longer be able to open it.`)) return;
    const res = await fetch(`${API}/pollen/investor/links/${l.id}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) { toast.success('Link revoked'); await load(); }
  };

  // Share URL: the /api/og/investor/<slug> path returns an HTML stub with
  // OG / Twitter card meta tags (rich preview = a clipping of pollendots.com)
  // and meta-refreshes the human to /connection/investor/<slug>. Pasting it
  // into WhatsApp / LinkedIn / Slack / Twitter shows the branded thumbnail.
  const openUrl = `${SHARE_ORIGIN}/api/og/investor/presentations`;
  const previewUrl = `${window.location.origin}/connection/investor/presentations`;
  const trackedShareUrl = (slug) => `${SHARE_ORIGIN}/api/og/investor/${slug}`;

  const shareNoteFor = (investorName, url) => {
    const greeting = investorName ? `Dear ${investorName},` : 'Hello,';
    return `${greeting}

Please find attached the Pollen Dots LLP investor brief — our pitch decks and the 3-year financial projection. The link below opens the documents directly; each PDF can be read on the page or downloaded.

${url}

Happy to schedule a call to walk you through the deck and answer any questions.

Warm regards,
Team Pollen Dots`;
  };

  /**
   * Primary share action for a tracked investor link.
   * On mobile / supported browsers: opens the native share sheet (WhatsApp,
   * Email, Messages …) with the cover note + URL pre-filled — the operator
   * picks the channel and sends in one tap.
   * Fallback: copies the note + URL to the clipboard so the operator can
   * paste it into any channel by hand.
   */
  const shareTrackedLink = async (investorName, slug) => {
    const url = trackedShareUrl(slug);
    const note = shareNoteFor(investorName, url);
    const title = `Pollen Dots LLP — Investor Brief${investorName ? ` · ${investorName}` : ''}`;
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        // NOTE: the URL is already embedded inside `note` (see shareNoteFor).
        // We deliberately do NOT pass a separate `url` field — WhatsApp /
        // Messages / Telegram append it on top of the text, which duplicates
        // the link. Passing only `text` lets the OS surface the embedded URL
        // for link previews while keeping the body clean.
        await navigator.share({ title, text: note });
        toast.success(`Shared ${investorName || 'investor link'}`);
        return;
      } catch (e) {
        // User cancelled the share sheet — silently swallow.
        if (e && e.name === 'AbortError') return;
        // Real failure — fall through to clipboard.
      }
    }
    await copyText(note, `Share note for ${investorName || 'investor'}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PollenDotsNav />
      <div className="max-w-4xl space-y-5" data-testid="investors-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Landmark className="w-6 h-6 text-amber-600" /> Investor Relations
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              One branded page with your decks &amp; projections — share it as a link.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => shareTrackedLink('', 'presentations')} data-testid="share-open-link-btn">
              <Share2 className="w-4 h-4 mr-1.5" /> Share with note
            </Button>
            <Button
              variant="outline"
              onClick={() => copyText(openUrl, 'Open investor link')}
              data-testid="copy-open-link-btn"
            >
              <Copy className="w-4 h-4 mr-1.5" /> Copy link only
            </Button>
            <Button variant="outline" onClick={() => window.open(previewUrl, '_blank')} data-testid="preview-page-btn">
              <ExternalLink className="w-4 h-4 mr-1.5" /> Preview
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground -mt-2" data-testid="open-link-stats">
          Open link: <span className="font-mono">{openUrl}</span>
          {' · '}{data?.page?.open_view_count || 0} views
          {data?.page?.last_open_view_at ? ` · last ${fmtDate(data.page.last_open_view_at)}` : ''}
        </p>

        {/* Cover note */}
        <Card data-testid="cover-note-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Cover note</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="Page headline"
              data-testid="headline-input"
            />
            <Textarea
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value)}
              rows={6}
              placeholder="The note investors read above your documents…"
              data-testid="cover-note-input"
            />
            <Button onClick={saveNote} disabled={savingNote} data-testid="save-cover-note-btn">
              {savingNote ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
              Save
            </Button>
          </CardContent>
        </Card>

        {/* Files */}
        <Card data-testid="investor-files-card">
          <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Documents ({data?.files?.length || 0})</CardTitle>
            <div>
              <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={onUpload} />
              <Button size="sm" variant="outline" disabled={uploading}
                onClick={() => fileInputRef.current?.click()} data-testid="upload-pdf-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                Add PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.files || []).map((f, idx) => (
              <div key={f.id} className="flex items-center gap-3 p-2 rounded-lg border border-border" data-testid={`investor-file-row-${f.id}`}>
                <img
                  src={`${API}/public/investor/thumb/${f.id}`}
                  alt={f.title}
                  className="w-16 h-12 object-cover rounded border border-border bg-muted shrink-0"
                />
                <div className="flex-1 min-w-0">
                  {editingFile?.id === f.id ? (
                    <div className="flex gap-2">
                      <Input
                        value={editingFile.title}
                        onChange={(e) => setEditingFile({ ...editingFile, title: e.target.value })}
                        onKeyDown={(e) => e.key === 'Enter' && renameFile()}
                        className="h-8"
                        autoFocus
                        data-testid="rename-file-input"
                      />
                      <Button size="sm" className="h-8" onClick={renameFile} data-testid="rename-file-save-btn">Save</Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium truncate">{f.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {f.page_count} pages · {fmtSize(f.size)}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditingFile({ id: f.id, title: f.title })} data-testid={`rename-file-btn-${f.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === 0} onClick={() => moveFile(idx, -1)} data-testid={`move-up-btn-${f.id}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === (data?.files?.length || 1) - 1} onClick={() => moveFile(idx, 1)} data-testid={`move-down-btn-${f.id}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteFile(f)} data-testid={`delete-file-btn-${f.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {(data?.files || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No documents yet — add your first PDF.</p>
            )}
          </CardContent>
        </Card>

        {/* Tracked links */}
        <Card data-testid="tracked-links-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="w-4 h-4 text-amber-600" /> Tracked investor links
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Give each investor their own link to see who viewed, read, or downloaded — and when.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={newInvestor}
                onChange={(e) => setNewInvestor(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createLink()}
                placeholder="Investor / firm name (e.g. Blume Ventures)"
                data-testid="new-investor-input"
              />
              <Button onClick={createLink} disabled={creatingLink} data-testid="create-tracked-link-btn">
                {creatingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create link'}
              </Button>
            </div>
            {(data?.links || []).map((l) => (
              <div key={l.id} className="p-3 rounded-lg border border-border space-y-2" data-testid={`tracked-link-row-${l.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">{l.investor_name}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge variant="secondary" className="gap-1 font-normal">
                      <Eye className="w-3 h-3" /> {l.view_count || 0}
                    </Badge>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => revokeLink(l)} data-testid={`revoke-link-btn-${l.id}`} title="Revoke link">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground font-mono truncate">{trackedShareUrl(l.slug)}</p>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {/* Primary action — opens native share sheet on mobile with note + link */}
                  <Button
                    size="sm"
                    onClick={() => shareTrackedLink(l.investor_name, l.slug)}
                    data-testid={`share-tracked-link-btn-${l.id}`}
                    className="h-8 gap-1.5"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    Share with note
                  </Button>
                  {/* Secondary — copy just the URL */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyText(trackedShareUrl(l.slug), `Link for ${l.investor_name}`)}
                    data-testid={`copy-tracked-link-btn-${l.id}`}
                    className="h-8 gap-1.5"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy link only
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {l.last_viewed_at ? `Last activity ${fmtDate(l.last_viewed_at)}` : 'Not opened yet'}
                  {(l.views || []).length > 0 && (
                    <> · recent: {(l.views || []).slice(-3).map(v => v.type === 'page_view' ? 'opened page' : `${v.type} ${v.file || ''}`).join(' · ')}</>
                  )}
                </p>
              </div>
            ))}
            {(data?.links || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No tracked links yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
